import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider } from "./components/CurrencySelector";
import { Toaster } from "./components/ui/sonner";
import { GlobalSidebar } from "./components/ui/GlobalSidebar";
import { CookieConsent } from "./components/CookieConsent";
import { FeatureGuard } from "./components/FeatureGuard";
import { AuthPage } from "./pages/AuthPage";
import { Dashboard } from "./pages/Dashboard";
import { VehicleDataPage } from "./pages/VehicleDataPage";
import { VehicleLibraryPage } from "./pages/VehicleLibraryPage";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminKnowledge } from "./pages/admin/AdminKnowledge";
import { AdminKnowledgeNew } from "./pages/admin/AdminKnowledgeNew";
import { AdminDocuments } from "./pages/admin/AdminDocuments";
import { AdminFeedbackAnalysis } from "./pages/admin/AdminFeedbackAnalysis";
import { AdminContentSuggestions } from "./pages/admin/AdminContentSuggestions";
import { KnowledgeDashboard } from "./pages/admin/KnowledgeDashboard";
import { AdminPlans } from "./pages/admin/AdminPlans";
import { AdminPlansNew } from "./pages/admin/AdminPlansNew";
import { AdminDiagnosePlans } from "./pages/admin/AdminDiagnosePlans";
import { AdminFMSPlans } from "./pages/admin/AdminFMSPlans";
import { AdminSettings } from "./pages/admin/AdminSettings";
import { AdminBranding } from "./pages/admin/AdminBranding";
import { AdminEmail } from "./pages/admin/AdminEmail";
import { AdminDisclaimer } from "./pages/admin/AdminDisclaimer";
import { FMSLandingPage } from "./pages/FMSLandingPage";
import { AdminFMSSettings } from "./pages/admin/AdminFMSSettings";
import { DemoPage } from "./pages/DemoPage";
import { ContactPage } from "./pages/ContactPage";
import { VehicleSelect } from "./pages/VehicleSelect";
import { VehiclesPage } from "./pages/VehiclesPage";
import { DiagnosisStart } from "./pages/DiagnosisStart";
import { DiagnosisSession } from "./pages/DiagnosisSession";
import { HistoryPage } from "./pages/HistoryPage";
import { LandingPage } from "./pages/LandingPage";
import { LandingPageNew } from "./pages/LandingPageNew";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { ReportPage } from "./pages/ReportPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SignupPage } from "./pages/SignupPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import AGBPage from "./pages/AGBPage";
import KontaktPage from "./pages/KontaktPage";
import CookieRichtliniePage from "./pages/CookieRichtliniePage";
import NewsletterConfirmPage from "./pages/NewsletterConfirmPage";
import NewsletterUnsubscribePage from "./pages/NewsletterUnsubscribePage";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminMarketingKI from "./pages/admin/AdminMarketingKI";
import { OBDLivePage } from "./pages/OBDLivePage";
import { AdminEarlyBird } from "./pages/admin/AdminEarlyBird";
import { AdminCookieConsents } from "./pages/admin/AdminCookieConsents";
import { AdminServiceDashboard } from "./pages/admin/AdminServiceDashboard";
import AdminVehicleImages from "./pages/admin/AdminVehicleImages";
import { AdminVehicleCatalog } from "./pages/admin/AdminVehicleCatalog";
import AdminTuningData from "./pages/admin/AdminTuningData";
import AdminVehicleMaster from "./pages/admin/AdminVehicleMaster";
import AdminVehicleEditor from "./pages/admin/AdminVehicleEditor";
import AdminCarCortex from "./pages/admin/AdminCarCortex";
import DebugViewer from "./pages/admin/DebugViewer";
import AdminLiveOBD from "./pages/admin/AdminLiveOBD";
import AdminDongleShop from "./pages/admin/AdminDongleShop";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminDataPipeline from "./pages/admin/AdminDataPipeline";
import TuningConfiguratorSettings from "./pages/TuningConfiguratorSettings";
import TuningConfiguratorEmbed from "./pages/TuningConfiguratorEmbed";
import { DownloadsPage } from "./pages/DownloadsPage";
import UserManual from "./pages/UserManual";
import FAQ from "./pages/FAQ";
import ReferralsPage from "./pages/ReferralsPage";
import VINDecoderPage from "./pages/VINDecoderPage";
import HilfeCenterPage from "./pages/HilfeCenterPage";
import StatusPage from "./pages/StatusPage";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div style={{ color: '#7CD4FD' }}>Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div style={{ color: '#7CD4FD' }}>Loading...</div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" />;
  if (!user.is_admin) return <Navigate to="/dashboard" />;
  
  return children;
};

function AppContent() {
  const location = useLocation();
  const publicPaths = ['/', '/fms', '/obd-live-detail', '/auth', '/signup', '/demo', '/contact', '/impressum', '/datenschutz', '/agb', '/kontakt', '/cookie-richtlinie', '/hilfe', '/status', '/faq', '/newsletter/confirm', '/newsletter/unsubscribe'];
  const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/newsletter/') || location.pathname.startsWith('/embed/');
  
  return (
    <>
      {!isPublicPath && <GlobalSidebar />}
      <div className={!isPublicPath ? 'ml-64' : ''} style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
        <Routes>
          {/* Public Pages - No Sidebar */}
          <Route path="/" element={<LandingPageNew />} />
          <Route path="/fms" element={<FMSLandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/agb" element={<AGBPage />} />
          <Route path="/kontakt" element={<KontaktPage />} />
          <Route path="/cookie-richtlinie" element={<CookieRichtliniePage />} />
          <Route path="/hilfe" element={<HilfeCenterPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
          
          {/* Embed Pages - No Sidebar, No Auth */}
          <Route path="/embed/tuning-configurator" element={<TuningConfiguratorEmbed />} />
          
          {/* Protected Pages - With Sidebar */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vehicle-data/:vehicleId" 
            element={
              <ProtectedRoute>
                <FeatureGuard feature="technical_specifications">
                  <VehicleDataPage />
                </FeatureGuard>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vehicle-library" 
            element={
              <ProtectedRoute>
                <FeatureGuard feature="vehicle_data_library">
                  <VehicleLibraryPage />
                </FeatureGuard>
              </ProtectedRoute>
            } 
          />
        
          {/* User Pages */}
          <Route 
            path="/vehicles" 
            element={
              <ProtectedRoute>
                <VehiclesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/history" 
            element={
              <ProtectedRoute>
                <HistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/report/:sessionId" 
            element={
              <ProtectedRoute>
                <ReportPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/subscription" 
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/invoices" 
            element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/manual" 
            element={
              <ProtectedRoute>
                <UserManual />
              </ProtectedRoute>
            } 
          />
          <Route path="/faq" element={<FAQ />} />
          <Route 
            path="/referrals" 
            element={
              <ProtectedRoute>
                <ReferralsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vin-decoder" 
            element={
              <ProtectedRoute>
                <VINDecoderPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tuning-configurator" 
            element={
              <ProtectedRoute>
                <FeatureGuard feature="tuning_configurator">
                  <TuningConfiguratorSettings />
                </FeatureGuard>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/downloads" 
            element={
              <ProtectedRoute>
                <DownloadsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/obd-live" 
            element={
              <ProtectedRoute>
                <FeatureGuard feature="obd_live_diagnosis">
                  <OBDLivePage />
                </FeatureGuard>
              </ProtectedRoute>
            } 
          />
        
          {/* Diagnosis Flow */}
          <Route 
            path="/diagnosis/new" 
            element={
              <ProtectedRoute>
                <VehicleSelect />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/diagnosis/start" 
            element={
              <ProtectedRoute>
                <DiagnosisStart />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/diagnosis/session/:sessionId" 
            element={
              <ProtectedRoute>
                <DiagnosisSession />
              </ProtectedRoute>
            } 
          />
        
          {/* Admin Routes */}
          <Route 
            path="/admin/users" 
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/knowledge" 
            element={
              <AdminRoute>
                <AdminKnowledgeNew />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/documents" 
            element={
              <AdminRoute>
                <AdminDocuments />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/feedback-analysis" 
            element={
              <AdminRoute>
                <AdminFeedbackAnalysis />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/content-suggestions" 
            element={
              <AdminRoute>
                <AdminContentSuggestions />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/knowledge-dashboard" 
            element={
              <AdminRoute>
                <KnowledgeDashboard />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/tuning-data" 
            element={
              <AdminRoute>
                <AdminTuningData />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/vehicle-master" 
            element={
              <AdminRoute>
                <AdminVehicleMaster />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/vehicle-editor" 
            element={
              <AdminRoute>
                <AdminVehicleEditor />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/carcortex" 
            element={
              <AdminRoute>
                <AdminCarCortex />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/debug" 
            element={
              <AdminRoute>
                <DebugViewer />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/live-obd" 
            element={
              <AdminRoute>
                <AdminLiveOBD />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/dongle-shop" 
            element={
              <AdminRoute>
                <AdminDongleShop />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/system" 
            element={
              <AdminRoute>
                <AdminSystem />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/analytics" 
            element={
              <AdminRoute>
                <AdminAnalytics />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/data-pipeline" 
            element={
              <AdminRoute>
                <AdminDataPipeline />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/plans" 
            element={
              <AdminRoute>
                <AdminPlansNew />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/diagnose-plans" 
            element={
              <AdminRoute>
                <AdminDiagnosePlans />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/fms-plans" 
            element={
              <AdminRoute>
                <AdminFMSPlans />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/services" 
            element={
              <AdminRoute>
                <AdminServiceDashboard />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/branding" 
            element={
              <AdminRoute>
                <AdminBranding />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/disclaimer" 
            element={
              <AdminRoute>
                <AdminDisclaimer />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/fms-settings" 
            element={
              <AdminRoute>
                <AdminFMSSettings />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/email" 
            element={
              <AdminRoute>
                <AdminEmail />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/marketing" 
            element={
              <AdminRoute>
                <AdminMarketing />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/marketing-ki" 
            element={
              <AdminRoute>
                <AdminMarketingKI />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/earlybird" 
            element={
              <AdminRoute>
                <AdminEarlyBird />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/cookie-consents" 
            element={
              <AdminRoute>
                <AdminCookieConsents />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/vehicle-images" 
            element={
              <AdminRoute>
                <AdminVehicleImages />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/vehicle-catalog" 
            element={
              <AdminRoute>
                <AdminVehicleCatalog />
              </AdminRoute>
            } 
          />
        </Routes>
        <Toaster />
        <CookieConsent />
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <AppContent />
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
